// What a searchable mat-select lists for what has been typed: the items whose text contains every
// word, plus - so the select can keep showing its value - the current item when a search filters it out.
export class SelectSearchState<T> {
  term = '';
  options: T[] = [];
  pinned = false;

  constructor(
    private readonly items: () => T[],
    private readonly label: (item: T) => string,
    private readonly current: () => T | undefined | null
  ) { }

  // Enter takes the highlighted option, which should be the first match rather than the pinned item.
  get firstMatchIndex(): number {
    return this.pinned ? 1 : 0;
  }

  get noMatches(): boolean {
    return this.term.trim() !== '' && this.options.length === 0;
  }

  setTerm(term: string) {
    this.term = term;
    this.refresh();
  }

  reset() {
    this.setTerm('');
  }

  // Call when the items change; the search itself calls it as the term changes.
  refresh() {
    const items = this.items();
    const words = this.term.toLowerCase().split(/\s+/).filter(word => word);

    if (!words.length) {
      this.options = items;
      this.pinned = false;
      return;
    }

    const matches = items.filter(item => {
      const text = this.label(item).toLowerCase();
      return words.every(word => text.includes(word));
    });

    // A select only shows its value while an option for it exists, so keep the current item listed
    // when a search filters it out or the field would look emptied while the user searches.
    const current = this.current();
    this.pinned = matches.length > 0
      && current !== undefined
      && current !== null
      && items.includes(current)
      && !matches.includes(current);
    this.options = this.pinned ? [current as T, ...matches] : matches;
  }
}
